import React, { useContext, useRef, useState } from 'react'
import './style/sidebar-bottom-menu.scss'

import { Dropdown, Menu, Modal } from 'antd'
import type { MenuProps } from 'antd'
import { CodeOutlined, LogoutOutlined, StarFilled } from '@ant-design/icons'
import { SidebarThemeToggle } from './sidebar-theme-toggle'
import { AppState } from '../../stores/app'
import { AppService } from '../../services'
import { AuthService } from '../../services'
import classnames from 'classnames'
import { Container } from 'typescript-ioc/es5'
import { observer } from 'mobx-react'
import { NoTranslate } from '../no-translate/no-translate'
import { useService } from '../../hooks/use-service'
import { PlatformUserContext, UserReviewStatus } from '../../providers/user-provider/user-provider'
import G2ColorIcon from '../icons/g2-icon-color'
import { AntdClassnameTokens } from '../../styles/antd-classname-tokens'
import AppMenu from './app-menu'
import { MenuIcon } from '@pushly/aqe/lib/icons'
import { SIDEBAR_ICON_SIZE } from './constants'

declare const PlatformNotifications: any

type MenuItem = NonNullable<MenuProps['items']>[number]

/** Space between the sidebar's edge and the user popup. */
const USER_MENU_EDGE_GAP = 2

const menuClassNames = AntdClassnameTokens.Menu({ prefix: 'sidebar' })

const SidebarBottomMenu: React.FC = observer(() => {
    const appState = Container.get(AppState)
    const appService = Container.get(AppService)
    const authService = useService<AuthService>(AuthService)
    const userCtx = useContext(PlatformUserContext)
    const appSvc = useService(AppService)

    const { currentUser } = appState
    const profilePath = `/users/${currentUser?.id}`

    const [userMenuOpen, setUserMenuOpen] = useState(false)
    const userAnchorRef = useRef<HTMLSpanElement>(null)
    const [userMenuOffset, setUserMenuOffset] = useState(0)

    // The row stops short of the sidebar's edge (the collapsed menu is a 50px column), so the
    // popup is pushed out by that distance to sit just off the edge, like the domain selector.
    const changeUserMenuOpen = (open: boolean) => {
        if (open) {
            const sidebar = document.querySelector('.app-sidebar')
            const anchor = userAnchorRef.current
            if (sidebar && anchor) {
                setUserMenuOffset(
                    sidebar.getBoundingClientRect().right - anchor.getBoundingClientRect().right + USER_MENU_EDGE_GAP,
                )
            }
        }
        setUserMenuOpen(open)
    }

    // G2 Review state
    const [reviewModalOpen, setReviewModalOpen] = useState(false)
    const domainHasReviewFlag = appState.currentDomain?.flags?.includes('FEAT_G2_IN_APP_REVIEW')
    const canShowG2 =
        userCtx.authenticated &&
        domainHasReviewFlag &&
        !('isInternalUser' in userCtx ? userCtx.user?.isInternalUser : true) &&
        ('reviewState' in userCtx
            ? userCtx.reviewState?.status !== UserReviewStatus.COMPLETED && userCtx.reviewState.url
            : false)

    const handleG2Click = () => {
        if (!userCtx.authenticated) return
        const nextStatus = UserReviewStatus.ACKNOWLEDGED
        if (userCtx.reviewState.status !== UserReviewStatus.DISMISSED) {
            appSvc.setG2ReviewStatus(userCtx.user.id, { status: nextStatus })
            userCtx.assignUser!((prev) => ({
                ...prev,
                reviewState: {
                    ...('reviewState' in prev ? prev.reviewState : {}),
                    status: nextStatus,
                },
            }))
        }
        setReviewModalOpen(true)
    }

    const handleClick: NonNullable<MenuProps['onClick']> = ({ key, domEvent }) => {
        switch (key) {
            case 'help':
                // Collapsed, the link's text is hidden and a click lands on the item instead.
                window.open(appState.documentationLink, '_blank', 'noopener,noreferrer')
                break
            case 'g2-review':
                handleG2Click()
                break
            case 'user-settings':
                if (domEvent.metaKey) {
                    window.open(profilePath)
                } else {
                    appService.route(profilePath)
                }
                break
            case 'register-preview-device':
                window.open('/register-preview-device', '_blank', 'noopener,noreferrer')
                break
            case 'logout':
                authService.logout(false).catch(() => {})
                break
        }
    }

    const userChildren: MenuItem[] = [
        ...(currentUser?.isInternalUser
            ? [
                  {
                      key: 'theme-toggle',
                      label: <SidebarThemeToggle />,
                      className: 'sidebar-theme-toggle-item',
                  },
                  { type: 'divider' as const, key: 'theme-divider' },
              ]
            : []),
        {
            key: 'user-settings',
            icon: <CodeOutlined aria-hidden="true" />,
            label: 'User Settings',
        },
        ...(PlatformNotifications.supported
            ? [
                  {
                      key: 'register-preview-device',
                      icon: <StarFilled aria-hidden="true" />,
                      label: 'Register Preview Device',
                  },
              ]
            : []),
        {
            key: 'logout',
            icon: <LogoutOutlined aria-hidden="true" />,
            label: 'Logout',
            danger: true,
        },
    ]

    const items: MenuItem[] = [
        ...(canShowG2
            ? [
                  {
                      key: 'g2-review',
                      className: 'sidebar-lower-item',
                      icon: <G2ColorIcon className="g2-icon" aria-hidden="true" />,
                      label: 'Review us on G2!',
                  } as MenuItem,
              ]
            : []),
        {
            key: 'help',
            className: 'sidebar-lower-item',
            icon: <MenuIcon name="help" size={SIDEBAR_ICON_SIZE} aria-hidden="true" />,
            label: (
                <a
                    href={appState.documentationLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                >
                    Help
                </a>
            ),
        },
        {
            key: 'user',
            className: 'sidebar-lower-item',
            // The collapsed menu's tooltip defaults to the label, which would render the
            // dropdown below a second time. A plain string keeps the label to one copy.
            // Hovering the popup counts as hovering this item (events bubble through portals), which
            // would keep its tooltip up, so there is none while the menu is open.
            title: userMenuOpen ? false : currentUser?.name,
            // Filled while its menu is open, as a pressed item is.
            icon: <MenuIcon name="account" size={SIDEBAR_ICON_SIZE} current={userMenuOpen} aria-hidden="true" />,
            label: (
                <>
                    <NoTranslate>{currentUser?.name}</NoTranslate>
                    <Dropdown
                        open={userMenuOpen}
                        onOpenChange={changeUserMenuOpen}
                        trigger={['click']}
                        // Sets the open animation: a top placement grows upward from the popup's bottom
                        // edge. The `align` below is what actually positions it.
                        placement="topLeft"
                        // Beside the sidebar, its bottom edge on the row's, like the submenu popup it replaces.
                        align={{
                            points: ['bl', 'br'],
                            offset: [userMenuOffset, 0],
                            overflow: { adjustX: true, adjustY: true },
                        }}
                        rootClassName={classnames('sidebar-user-dropdown', {
                            'sidebar-submenu-org-context': appState.inOrgContext,
                        })}
                        popupRender={() => (
                            <Menu
                                className="sidebar-user-menu"
                                // Dropdown renames a nested Menu's classes to `ant-dropdown-menu-*`; the
                                // popup styles are written for `ant-menu-*`.
                                prefixCls="ant-menu"
                                // The Sider is collapsed when this is opened from the collapsed sidebar, and a
                                // nested Menu inherits that: icon-only items with tooltips. An explicit
                                // `inlineCollapsed` wins over the Sider. (`mode` only satisfies antd's check
                                // that the prop is used with an inline menu; Dropdown forces it vertical.)
                                mode="inline"
                                inlineCollapsed={false}
                                tooltip={false}
                                theme="dark"
                                selectable={false}
                                items={userChildren}
                                onClick={(info) => {
                                    changeUserMenuOpen(false)
                                    handleClick(info)
                                }}
                            />
                        )}
                    >
                        <span ref={userAnchorRef} className="sidebar-user-anchor" />
                    </Dropdown>
                </>
            ),
        },
    ]

    return (
        <>
            <AppMenu
                placement="bottom"
                extraItems={items}
                onExtraClick={(info) => {
                    // A keyboard activation of the user row; a mouse click is the dropdown's own trigger.
                    if (info.key === 'user') {
                        if (info.domEvent.type !== 'click') changeUserMenuOpen(true)
                        return
                    }
                    handleClick(info)
                }}
            />

            {canShowG2 && (
                <Modal
                    className="g2-review-modal"
                    open={reviewModalOpen}
                    onCancel={() => setReviewModalOpen(false)}
                    closable
                    footer={null}
                >
                    <iframe src={userCtx.reviewState.url} />
                </Modal>
            )}
        </>
    )
})

export default SidebarBottomMenu
